import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Copy, Zap, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { PLATFORM_META } from "@/lib/platforms";

const FAQ_ITEMS = [
  {
    q: "How does PostShifted work?",
    a: "Paste any long-form content — a blog post, transcript, or voice memo. PostShifted uses Anthropic Claude Haiku 4.5 to rewrite it as a Twitter/X thread, LinkedIn post, LinkedIn carousel, Instagram caption, and email newsletter. Each format uses its own platform-native prompt for length, tone, and structure.",
  },
  {
    q: "How much does it cost?",
    a: "Free forever for casual creators — 5 remixes every month, no card. Pro is $19 USD per month for unlimited remixes, 40,000 character inputs, and priority generation. Every Pro subscription starts with a 7-day free trial. Cancel anytime.",
  },
  {
    q: "Can I cancel my subscription anytime?",
    a: "Yes. You can cancel from Settings → Billing → Manage subscription in one click. You keep Pro access until the end of your billing period. No emails or phone calls required.",
  },
  {
    q: "What's your refund policy?",
    a: "Full refund within 7 days of purchase or renewal if you've generated 50 remixes or fewer during that cycle. See the full Refund Policy for details.",
  },
  {
    q: "Who handles payments?",
    a: "Payments and invoicing are handled by PayPal. When you subscribe, PayPal charges your PayPal account or connected card and we never see or store your card details.",
  },
  {
    q: "Do you train AI models on my content?",
    a: "No. Your input and outputs belong to you. We do not use your submitted content to train our own or any third-party AI models.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-12 lg:px-10 lg:pb-24 lg:pt-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <p className="mb-6 text-xs uppercase tracking-[0.25em] text-neutral-500" data-testid="hero-eyebrow">
              — Content, remixed
            </p>
            <h1
              data-testid="hero-title"
              className="font-serif text-[2.5rem] font-bold leading-[1.05] tracking-tight text-neutral-900 sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Turn one piece of content into{" "}
              <em className="text-coral">posts for every platform.</em>
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-6 max-w-xl text-base leading-relaxed text-neutral-600 sm:mt-8 sm:text-lg"
            >
              Paste a blog post, transcript, or voice memo. PostShifted turns it into a Twitter
              thread, LinkedIn post, carousel, Instagram caption, and email newsletter — each written
              to feel platform-native. In under a minute.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <Link to="/signup" data-testid="hero-cta-primary" className="sm:w-auto">
                <Button
                  size="lg"
                  className="h-12 w-full rounded-[3px] bg-coral px-8 text-white shadow-none transition-all duration-300 hover:-translate-y-[1px] hover:bg-coral-hover hover:shadow-coral sm:w-auto"
                >
                  Start free — 5 remixes
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-cta-secondary" className="sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 w-full rounded-[3px] border-neutral-300 px-8 text-neutral-900 hover:border-neutral-900 hover:bg-white sm:w-auto"
                >
                  See pricing
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-widest text-neutral-500 sm:mt-14 sm:gap-x-8 sm:text-xs">
              <span>· Claude Haiku 4.5</span>
              <span>· No credit card</span>
              <span>· Copy-paste ready</span>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="border border-neutral-200 bg-white p-6 shadow-soft sm:p-8">
              <p className="mb-5 text-xs uppercase tracking-widest text-neutral-500">The remix</p>
              <div className="space-y-3.5 font-serif text-base sm:text-lg">
                <div className="border-b border-neutral-100 pb-3">
                  <span className="text-neutral-400">01.</span>
                  <span className="ml-3">Paste your long-form.</span>
                </div>
                <div className="border-b border-neutral-100 pb-3">
                  <span className="text-neutral-400">02.</span>
                  <span className="ml-3">Pick the channels.</span>
                </div>
                <div className="border-b border-neutral-100 pb-3">
                  <span className="text-neutral-400">03.</span>
                  <span className="ml-3">Generate.</span>
                </div>
                <div>
                  <span className="text-neutral-400">04.</span>
                  <span className="ml-3">Copy. Ship. Repeat.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formats */}
      <section className="border-t border-neutral-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-28">
          <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end md:gap-8 lg:mb-14">
            <div>
              <p className="mb-3 text-xs uppercase tracking-[0.25em] text-neutral-500">The formats</p>
              <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                Five outputs.<br />One click.
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-neutral-600 sm:text-base">
              Each format is tuned with a platform-native prompt. Length, tone, and structure are
              handled for you.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-px bg-neutral-200 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(PLATFORM_META).map(([key, meta], i) => (
              <div key={key} className="group bg-white p-6 transition-colors hover:bg-neutral-50 sm:p-8">
                <div className="mb-5 flex items-baseline justify-between">
                  <span className="font-serif text-4xl italic text-neutral-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs uppercase tracking-widest text-neutral-400">Format</span>
                </div>
                <h3 className="font-serif text-2xl font-semibold tracking-tight">{meta.label}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">{meta.description}</p>
              </div>
            ))}
            <div className="hidden bg-white p-8 lg:block">
              <p className="mb-5 text-xs uppercase tracking-widest text-neutral-400">More soon</p>
              <p className="font-serif text-2xl italic text-neutral-400">YouTube · Blog · Reddit</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="border-t border-neutral-200">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-16 lg:grid-cols-3 lg:gap-16 lg:px-10 lg:py-28">
          {[
            { icon: Sparkles, title: "Human, not AI-y", body: "Prompted to skip the giveaway phrases. Reads like a person wrote it because a person guided it." },
            { icon: Zap, title: "Fast by design", body: "Claude Haiku 4.5 runs all formats in parallel. Coffee-break turnaround, not lunch-break." },
            { icon: Copy, title: "Copy-paste ready", body: "No cleanup. No 'as an AI'. Just the post, ready for the compose box." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="border-t border-neutral-900 pt-6 lg:pt-8">
              <Icon className="mb-6 h-5 w-5 text-coral lg:h-6 lg:w-6" strokeWidth={1.5} />
              <h3 className="font-serif text-xl font-semibold tracking-tight sm:text-2xl">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing preview */}
      <section className="border-t border-neutral-200 bg-white" id="pricing-preview">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="mb-10 text-center">
            <p className="mb-3 text-xs uppercase tracking-[0.25em] text-neutral-500">— Simple pricing</p>
            <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              One price. <em className="text-coral">No surprises.</em>
            </h2>
            <p className="mx-auto mt-4 max-w-md text-sm text-neutral-600 sm:text-base">
              Start free. Upgrade the day you want unlimited remixes. Cancel anytime.
            </p>
          </div>

          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">
            {/* Free */}
            <div
              className="border border-neutral-200 bg-white p-6 shadow-soft sm:p-8"
              data-testid="landing-pricing-free"
            >
              <div className="mb-5 flex items-baseline justify-between border-b border-neutral-200 pb-4">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-neutral-500">Starter</p>
                  <h3 className="mt-1 font-serif text-2xl font-bold tracking-tight">Free</h3>
                </div>
                <span className="font-serif text-4xl font-bold">$0</span>
              </div>
              <ul className="space-y-2.5 text-sm text-neutral-700">
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.5} />
                  5 remixes per month
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.5} />
                  All 5 output formats
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.5} />
                  History, copy & regenerate
                </li>
              </ul>
            </div>

            {/* Pro */}
            <div
              className="relative border-2 border-coral bg-white p-6 shadow-soft sm:p-8"
              data-testid="landing-pricing-pro"
            >
              <span className="absolute -top-3 left-6 bg-coral px-3 py-1 text-[10px] uppercase tracking-widest text-white">
                Most popular
              </span>
              <div className="mb-5 flex items-baseline justify-between border-b border-neutral-200 pb-4">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-coral">Pro</p>
                  <h3 className="mt-1 font-serif text-2xl font-bold tracking-tight">Unlimited</h3>
                </div>
                <div className="text-right">
                  <span className="font-serif text-4xl font-bold">$19</span>
                  <div className="text-[11px] text-neutral-500">/ month</div>
                </div>
              </div>
              <ul className="space-y-2.5 text-sm text-neutral-700">
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-coral" strokeWidth={2} />
                  Unlimited remixes
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-coral" strokeWidth={2} />
                  40,000 characters per input
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-coral" strokeWidth={2} />
                  Priority generation
                </li>
                <li className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-coral" strokeWidth={2} />
                  Custom Templates + Brand Voice
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <Link to="/pricing" data-testid="landing-see-full-pricing">
              <Button
                variant="outline"
                className="h-11 rounded-[3px] border-neutral-300 px-6 hover:border-neutral-900"
              >
                Full pricing details
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <p className="mt-4 text-xs text-neutral-500">
              Billed monthly via PayPal. All prices in USD.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-neutral-200" data-testid="landing-faq">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:px-10 lg:py-24">
          <div className="mb-10 text-center">
            <p className="mb-3 text-xs uppercase tracking-[0.25em] text-neutral-500">— Common questions</p>
            <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl">
              Everything you might ask.
            </h2>
          </div>

          <dl className="divide-y divide-neutral-200 border-y border-neutral-200">
            {FAQ_ITEMS.map((item, i) => (
              <div
                key={item.q}
                className="grid grid-cols-1 gap-4 py-6 md:grid-cols-12 md:gap-8 lg:py-8"
              >
                <dt className="md:col-span-4">
                  <span className="mr-3 font-serif italic text-neutral-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-lg font-semibold tracking-tight text-neutral-900 sm:text-xl">
                    {item.q}
                  </span>
                </dt>
                <dd className="text-sm leading-relaxed text-neutral-600 md:col-span-8 md:text-base">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-10 text-center text-sm text-neutral-600">
            Anything else?{" "}
            <Link to="/contact" className="text-coral underline underline-offset-4 hover:text-coral-hover">
              Ask us here
            </Link>
            {" — "}we reply within 1 business day.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-neutral-200 bg-neutral-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-28">
          <h2 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Write once.<br /><em className="text-coral">Publish everywhere.</em>
          </h2>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-neutral-400 sm:mt-8 sm:text-base">
            Five free remixes every month. No card. No trial. Just paste and go.
          </p>
          <Link to="/signup" data-testid="footer-cta">
            <Button
              size="lg"
              className="mt-8 h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-coral-hover hover:shadow-coral sm:mt-10"
            >
              Get started
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-neutral-200 bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-5 px-6 text-xs text-neutral-500 md:flex-row md:items-center lg:px-10">
          <span className="font-serif text-lg font-bold text-neutral-900">
            PostShifted<span className="text-coral">.</span>
          </span>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/terms" data-testid="landing-footer-terms" className="hover:text-neutral-900">Terms of Service</Link>
            <Link to="/privacy" data-testid="landing-footer-privacy" className="hover:text-neutral-900">Privacy Policy</Link>
            <Link to="/refund" data-testid="landing-footer-refund" className="hover:text-neutral-900">Refund Policy</Link>
            <Link to="/pricing" className="hover:text-neutral-900">Pricing</Link>
            <Link to="/contact" className="hover:text-neutral-900">Contact</Link>
          </nav>
          <span>© {new Date().getFullYear()} — Content, remixed.</span>
        </div>
      </footer>
    </div>
  );
}
